import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { proposalsAPI } from '../services/api'
import {
  ArrowLeft, Calendar, Euro, Building, Clock,
  CheckCircle, XCircle, AlertCircle, Eye,
  FileText, Send, Briefcase, TrendingUp
} from 'lucide-react'

const MyProposalsPage = ({ onNavigate } = {}) => {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all') // all, submitted, accepted, rejected

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'creator') {
      navigate('/dashboard')
      return
    }

    fetchMyProposals()
  }, [isAuthenticated, user])

  const fetchMyProposals = async () => {
    try {
      setLoading(true)
      const response = await proposalsAPI.getMyProposals()
      console.log('My proposals response:', response.data)
      setProposals(response.data.proposals || [])
    } catch (error) {
      console.error('Error fetching proposals:', error)
      setError('Er is een fout opgetreden bij het laden van je voorstellen')
    } finally {
      setLoading(false)
    }
  }

  const getStatusConfig = (status) => {
    const configs = {
      draft: {
        label: 'Concept',
        icon: FileText,
        color: 'text-gray-400',
        bg: 'bg-gray-100',
        border: 'border-gray-200'
      },
      submitted: {
        label: 'Ingediend',
        icon: Send,
        color: 'text-blue-400',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      },
      'under_review': {
        label: 'In Behandeling',
        icon: Eye,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200'
      },
      shortlisted: {
        label: 'Shortlist',
        icon: TrendingUp,
        color: 'text-[#7c3aed]',
        bg: 'bg-purple-50',
        border: 'border-purple-200'
      },
      accepted: {
        label: 'Geaccepteerd',
        icon: CheckCircle,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      },
      rejected: {
        label: 'Afgewezen',
        icon: XCircle,
        color: 'text-red-500',
        bg: 'bg-red-50',
        border: 'border-red-200'
      },
      withdrawn: {
        label: 'Ingetrokken',
        icon: AlertCircle,
        color: 'text-orange-400',
        bg: 'bg-orange-50',
        border: 'border-orange-200'
      }
    }
    return configs[status] || configs.draft
  }

  const filteredProposals = proposals.filter(proposal => {
    if (filter === 'all') return true
    if (filter === 'submitted') return ['submitted', 'under_review', 'shortlisted'].includes(proposal.status)
    if (filter === 'accepted') return proposal.status === 'accepted'
    if (filter === 'rejected') return proposal.status === 'rejected'
    return true
  })

  const stats = {
    total: proposals.length,
    pending: proposals.filter(p => ['submitted', 'under_review', 'shortlisted'].includes(p.status)).length,
    accepted: proposals.filter(p => p.status === 'accepted').length,
    rejected: proposals.filter(p => p.status === 'rejected').length
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1D8BF8] mx-auto mb-4"></div>
          <p className="text-[#475569]">Voorstellen laden...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="p-2.5 -ml-2 hover:bg-[#F8FAFC] rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold">Mijn Voorstellen</h1>
                <p className="text-sm text-[#475569]">
                  Overzicht van al je ingediende proposals
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8">
          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#3EB489]" />
              <span className="text-2xl md:text-3xl font-bold">{stats.total}</span>
            </div>
            <p className="text-sm text-[#475569]">Totaal Voorstellen</p>
          </div>

          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6 md:w-8 md:h-8 text-yellow-600" />
              <span className="text-2xl md:text-3xl font-bold">{stats.pending}</span>
            </div>
            <p className="text-sm text-[#475569]">In Behandeling</p>
          </div>

          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold">{stats.accepted}</span>
            </div>
            <p className="text-sm text-[#475569]">Geaccepteerd</p>
          </div>

          <div className="p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
              <span className="text-2xl md:text-3xl font-bold">{stats.rejected}</span>
            </div>
            <p className="text-sm text-[#475569]">Afgewezen</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
              filter === 'all'
                ? 'bg-sky-500 text-white'
                : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
            }`}
          >
            Alle ({proposals.length})
          </button>
          <button
            onClick={() => setFilter('submitted')}
            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
              filter === 'submitted'
                ? 'bg-sky-500 text-white'
                : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
            }`}
          >
            Ingediend ({stats.pending})
          </button>
          <button
            onClick={() => setFilter('accepted')}
            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
              filter === 'accepted'
                ? 'bg-sky-500 text-white'
                : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
            }`}
          >
            Geaccepteerd ({stats.accepted})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
              filter === 'rejected'
                ? 'bg-sky-500 text-white'
                : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
            }`}
          >
            Afgewezen ({stats.rejected})
          </button>
        </div>

        {/* Proposals List */}
        {filteredProposals.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Nog geen voorstellen</h3>
            <p className="text-[#475569] mb-6">
              {filter === 'all'
                ? 'Je hebt nog geen voorstellen ingediend'
                : `Geen voorstellen met status: ${filter}`
              }
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EB489] rounded-lg font-semibold hover:bg-[#359E78] transition text-white"
            >
              <Briefcase className="w-5 h-5" />
              Zoek Opdrachten
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProposals.map((proposal) => {
              const statusConfig = getStatusConfig(proposal.status)
              const StatusIcon = statusConfig.icon
              const project = proposal.projectId || proposal.project
              const projectId = project?._id || project
              // Meerdere creators per opdracht: na acceptatie heeft de creator een eigen
              // kindproject (workspace). Legacy-voorstellen zonder childProject → de opdracht zelf.
              const workspaceId = proposal.childProject?._id || proposal.childProject?.id || projectId
              const wachtOpAanbetaling = proposal.status === 'accepted' && proposal.childProject && !proposal.childProject.depositPaid
              const openWorkspace = (e) => {
                e.preventDefault()
                e.stopPropagation()
                if (onNavigate) onNavigate('project-workspace', workspaceId)
                else navigate(`/project-workspace/${workspaceId}`)
              }

              // If project is deleted, show as non-clickable card
              if (!projectId) {
                return (
                  <div key={proposal._id} className="block">
                    <div className="p-6 rounded-2xl bg-white shadow-md border border-gray-200 opacity-60">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold mb-2 text-[#475569]">
                            Project verwijderd
                          </h3>
                          <p className="text-sm text-[#475569] mb-4">
                            Dit project is niet meer beschikbaar
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-[#475569]/50">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>
                                Ingediend op {new Date(proposal.createdAt).toLocaleDateString('nl-NL')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg ${statusConfig.bg} ${statusConfig.border} border shrink-0 self-start`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                          <span className={`font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <Link
                  key={proposal._id}
                  to={`/projects/${projectId}`}
                  className="block group"
                >
                  <div className="p-6 rounded-2xl bg-white shadow-md border border-gray-200 hover:border-[#1D8BF8]/50 transition-all">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Project Title */}
                        <h3 className="text-xl font-bold mb-2 break-words group-hover:text-[#3EB489] transition">
                          {project?.title || 'Project verwijderd'}
                        </h3>

                        {/* Project Details */}
                        {project && (
                          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-[#475569]">
                            {project.owner?.companyName && (
                              <div className="flex items-center gap-1">
                                <Building className="w-4 h-4" />
                                <span>{project.owner.companyName}</span>
                              </div>
                            )}
                            {project.budget && (
                              <div className="flex items-center gap-1">
                                <Euro className="w-4 h-4" />
                                <span>Budget: {(project.budget * 1.21).toFixed(2)} <span className="text-[#475569]/50 text-xs">incl. BTW</span></span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                Ingediend op {new Date(proposal.createdAt).toLocaleDateString('nl-NL')}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Proposal Amount */}
                        {proposal.amount && (
                          <div className="mb-3">
                            <span className="text-sm text-[#475569]">Jouw bod: </span>
                            <span className="text-lg font-bold text-[#3EB489]">
                              {proposal.amount}
                            </span>
                          </div>
                        )}

                        {/* Motivation Preview */}
                        {proposal.motivation && (
                          <p className="text-sm text-[#475569] line-clamp-2 mb-4">
                            {proposal.motivation}
                          </p>
                        )}

                        {/* Additional Info */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-[#475569]/50">
                          {proposal.companyViewed && (
                            <div className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              <span>Gezien door bedrijf</span>
                            </div>
                          )}
                          {proposal.responseTime && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>Reactietijd: {Math.round(proposal.responseTime)}u</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Badge — op mobiel als rij onder de inhoud, vanaf sm als rechterkolom */}
                      <div className="flex flex-row flex-wrap sm:flex-col items-center sm:items-end w-full sm:w-auto gap-2 sm:gap-3 shrink-0">
                        <div className={`flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg ${statusConfig.bg} ${statusConfig.border} border`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                          <span className={`font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        {proposal.status === 'accepted' && workspaceId && (
                          <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
                            {wachtOpAanbetaling && (
                              <span className="text-xs text-[#475569] text-left sm:text-right">Wacht op aanbetaling van het bedrijf</span>
                            )}
                            <button
                              type="button"
                              onClick={openWorkspace}
                              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 rounded-lg border border-[#3EB489] text-[#3EB489] text-sm font-semibold hover:bg-emerald-50 transition"
                            >
                              <Briefcase className="w-4 h-4" />
                              Naar je workspace
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Feedback if rejected */}
                    {proposal.status === 'rejected' && proposal.rejectionReason && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-500">
                          <strong>Feedback:</strong> {proposal.rejectionReason}
                        </p>
                      </div>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyProposalsPage
